import { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";

export default function MotoGallery({ images, alt = "" }) {
  const [active, setActive] = useState(0);
  const [dir, setDir] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchX = useRef(null);

  const multiple = images.length > 1;

  const go = useCallback(
    (delta) => {
      setDir(delta);
      setActive((p) => (p + delta + images.length) % images.length);
    },
    [images.length]
  );

  const prev = useCallback((e) => { e?.stopPropagation?.(); go(-1); }, [go]);
  const next = useCallback((e) => { e?.stopPropagation?.(); go(1); }, [go]);

  // Keyboard controls (only when lightbox open)
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, prev, next]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null || !multiple) return;
    const diff = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(diff) > 40) diff > 0 ? prev() : next();
    touchX.current = null;
  };

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
  };

  const ArrowBtn = ({ side, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={side === "left" ? "Vorige foto" : "Volgende foto"}
      className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-3" : "right-3"} z-10
        bg-black/50 hover:bg-[#E10600] text-white p-2.5 rounded-full backdrop-blur-sm
        transition-colors duration-200`}
    >
      {side === "left" ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
    </button>
  );

  return (
    <div>
      {/* Main image */}
      <div
        data-testid="gallery-main"
        className="relative aspect-[4/3] overflow-hidden border border-[#2A2B31] bg-[#141518] mb-3 group cursor-zoom-in"
        onClick={() => setLightbox(true)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.img
            key={active}
            src={images[active]}
            alt={alt}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        <span className="absolute top-3 right-3 z-10 bg-black/50 text-white p-2 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
          <Expand size={16} />
        </span>

        {multiple && (
          <>
            <ArrowBtn side="left" onClick={prev} testid="gallery-prev" />
            <ArrowBtn side="right" onClick={next} testid="gallery-next" />
            <div className="absolute bottom-3 right-3 z-10 bg-black/50 text-white text-xs font-mono-tt px-2.5 py-1 rounded-full backdrop-blur-sm">
              {active + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {multiple && (
        <div className="grid grid-cols-5 gap-3">
          {images.map((img, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => { setDir(i > active ? 1 : -1); setActive(i); }}
              className={`aspect-square overflow-hidden border-2 transition-colors ${active === i ? "border-[#E10600]" : "border-[#2A2B31] hover:border-[#5A5C63]"}`}
            >
              <img src={img} alt={`${alt} foto ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4 sm:p-8"
            onClick={() => setLightbox(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
              aria-label="Sluiten"
              className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-[#E10600] text-white p-3 rounded-full transition-colors"
            >
              <X size={24} />
            </button>

            {multiple && (
              <>
                <ArrowBtn side="left" onClick={prev} testid="lightbox-prev" />
                <ArrowBtn side="right" onClick={next} testid="lightbox-next" />
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 bg-black/50 text-white text-sm font-mono-tt px-3 py-1 rounded-full">
                  {active + 1} / {images.length}
                </div>
              </>
            )}

            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.img
                key={active}
                src={images[active]}
                alt={alt}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-full object-contain"
              />
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
